import { useEffect, useMemo, useState, useCallback } from 'react'
import type { MouseEvent } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  AlarmClock,
  ArrowLeft,
  Clock,
  Download,
  FileText,
  Link2,
  PlayCircle,
  Search,
  Video,
  BookOpen,
  CheckCircle,
  Layers,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/textarea'
import { Modal } from '@/components/ui/Modal'
import { FileUpload } from '@/components/ui/FileUpload'
import { useCourse, useCourseModuleResources, useCourseModules, useUploadCourseResources } from '@/hooks/useCourses'
import {
  useCompleteCourseModule,
  useLearnerCourseProgress,
} from '@/hooks/useLearnerCourses'
import { CourseModuleModel, CourseResourceModel } from '@/models/course.model'
import { useAuth } from '@/contexts/AuthContext'
import { paths } from '@/routes/path'
import { UserRole } from '@/models/user.model'
import { integrations } from '@/config/integrations'
import { useQuizzes } from '@/hooks/useQuizzes'

function getResourceIcon(resource: CourseResourceModel) {
  const type = resource.type?.toLowerCase() ?? ''
  if (type.includes('video')) return <Video className="h-4 w-4" />
  if (type.includes('pdf') || type.includes('doc') || type.includes('document')) return <FileText className="h-4 w-4" />
  if (type.includes('link')) return <Link2 className="h-4 w-4" />
  return <BookOpen className="h-4 w-4" />
}

function formatFileSize(bytes?: number) {
  if (!bytes || bytes <= 0) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}

function formatQuizDueDate(dueDate?: string | null) {
  if (!dueDate) return 'No due date'
  const date = new Date(dueDate)
  if (Number.isNaN(date.getTime())) return 'No due date'
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(date)
}

function formatQuizTimeLimit(seconds?: number | null) {
  if (!seconds) return 'No timer'
  const minutes = Math.max(1, Math.round(seconds / 60))
  return `${minutes} min`
}

function ResourceList({ title, resources }: { title: string; resources: CourseResourceModel[] }) {
  if (resources.length === 0) return null

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-secondary-700">{title}</h3>
      <div className="space-y-2">
        {resources.map((resource) => (
          <div
            key={resource.id}
            className="flex items-center justify-between rounded-2xl border border-secondary-100 bg-white px-4 py-3 shadow-sm transition hover:border-primary-200 hover:bg-primary-50"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                {getResourceIcon(resource)}
              </span>
              <div>
                <p className="font-medium text-secondary-900">{resource.title}</p>
                <p className="text-xs text-secondary-500">
                  {resource.mimeType ?? resource.type ?? 'Resource'} {formatFileSize(resource.fileSize)}
                </p>
              </div>
            </div>
            {resource.url && (
              <a
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:underline"
              >
                <Download className="h-4 w-4" />
                Download
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export function CourseContentPage() {
  const { courseId } = useParams<{ courseId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const isLearner = user?.role === UserRole.LEARNER

  const [resourceQuery, setResourceQuery] = useState('')
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadType, setUploadType] = useState('document')
  const [uploadFiles, setUploadFiles] = useState<File[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null)
  const [moduleActionError, setModuleActionError] = useState<string | null>(null)

  const { data: course, isLoading, error } = useCourse(courseId)
  const {
    data: courseModules,
    isLoading: isModulesLoading,
    error: modulesError,
  } = useCourseModules(courseId)
  const {
    data: learnerProgress,
    isLoading: isLearnerProgressLoading,
    error: learnerProgressError,
    refetch: refetchLearnerProgress,
  } = useLearnerCourseProgress(courseId, { enabled: isLearner })
  const uploadResourcesMutation = useUploadCourseResources(courseId)
  const completeModuleMutation = useCompleteCourseModule()

  const activeModuleId = searchParams.get('moduleId')

  const modules = useMemo(() => {
    if (isLearner && learnerProgress?.modules?.length) {
      return learnerProgress.modules
    }
    return courseModules ?? []
  }, [isLearner, learnerProgress, courseModules])

  const sortedModules = useMemo(
    () => modules.slice().sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)),
    [modules],
  )

  const activeModule = useMemo(() => {
    if (!sortedModules.length) return undefined
    if (activeModuleId) {
      return sortedModules.find((module) => module.id === activeModuleId) ?? sortedModules[0]
    }
    return sortedModules[0]
  }, [sortedModules, activeModuleId])

  const {
    data: remoteModuleResources,
    isLoading: isModuleResourcesLoading,
    error: moduleResourcesError,
  } = useCourseModuleResources(courseId, activeModule?.id)

  const moduleResources = useMemo(() => {
    if (remoteModuleResources) {
      return remoteModuleResources
    }
    return activeModule?.resources ?? []
  }, [remoteModuleResources, activeModule])
  const { videoResource, downloads, links } = useMemo(() => {
    const bucket = {
      videoResource: undefined as CourseResourceModel | undefined,
      downloads: [] as CourseResourceModel[],
      links: [] as CourseResourceModel[],
    }

    moduleResources.forEach((resource) => {
      const type = resource.type?.toLowerCase() ?? ''
      if (!bucket.videoResource && type.includes('video')) {
        bucket.videoResource = resource
        return
      }
      if (type.includes('pdf') || type.includes('document') || type.includes('doc')) {
        bucket.downloads.push(resource)
        return
      }
      bucket.links.push(resource)
    })

    return bucket
  }, [moduleResources])

  const filteredDownloads = downloads.filter((resource) =>
    resource.title.toLowerCase().includes(resourceQuery.toLowerCase()),
  )
  const filteredLinks = links.filter((resource) => resource.title.toLowerCase().includes(resourceQuery.toLowerCase()))

  const currentIndex = activeModule ? sortedModules.findIndex((module) => module.id === activeModule.id) : -1
  const previousModule = currentIndex > 0 ? sortedModules[currentIndex - 1] : undefined
  const nextModule = currentIndex >= 0 && currentIndex < sortedModules.length - 1 ? sortedModules[currentIndex + 1] : undefined
  const activeModuleProgress = activeModule?.progress
  const isActiveModuleCompleted =
    Boolean(activeModuleProgress?.isCompleted) ||
    activeModuleProgress?.status?.toLowerCase() === 'completed'

  const markCompletedPending = completeModuleMutation.isPending

  const quizFilters = useMemo(
    () => ({
      courseId: courseId ?? undefined,
      isActive: true,
      perPage: 50,
    }),
    [courseId],
  )
  const {
    data: quizzesData,
    isLoading: isQuizzesLoading,
  } = useQuizzes(quizFilters)
  const availableQuizzes = quizzesData?.quizzes ?? []

  const handlePlayerContextMenu = useCallback((event: MouseEvent<HTMLIFrameElement>) => {
    event.preventDefault()
  }, [])

  const streamLibraryId = integrations.BUNNY_STREAM_LIBRARY_ID //course.streamLibraryId
  const storageKey = videoResource?.storageKey
  const embedUrl =
    (streamLibraryId && storageKey) ? `https://iframe.mediadelivery.net/embed/${streamLibraryId}/${storageKey}` : undefined
  const fallbackVideoUrl = videoResource?.url

  const setModule = (module: CourseModuleModel | undefined) => {
    if (!module) return
    setSearchParams((params) => {
      const next = new URLSearchParams(params)
      next.set('moduleId', module.id)
      return next
    })
    setModuleActionError(null)
  }

  useEffect(() => {
    if (!activeModuleId && sortedModules.length > 0) {
      setSearchParams((params) => {
        const next = new URLSearchParams(params)
        next.set('moduleId', sortedModules[0].id)
        return next
      })
    }
  }, [activeModuleId, sortedModules, setSearchParams])

  const handleMarkModuleCompleted = async () => {
    if (!isLearner || !courseId || !activeModule || isActiveModuleCompleted) {
      return
    }
    setModuleActionError(null)
    try {
      await completeModuleMutation.mutateAsync({
        courseId,
        moduleId: activeModule.id,
        payload: {
          timeSpentSeconds: activeModuleProgress?.timeSpentSeconds ?? 0,
        },
      })
      if (isLearner) {
        await refetchLearnerProgress()
      }
    } catch (mutationError) {
      setModuleActionError((mutationError as Error)?.message || 'Unable to mark module as completed.')
    }
  }

  if (!courseId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-secondary-500">Invalid course module.</div>
    )
  }

  if (error || modulesError || moduleResourcesError || (isLearner && learnerProgressError)) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-secondary-500">
        <BookOpen className="mb-4 h-12 w-12 text-primary-400" />
        <p className="text-lg font-semibold">Unable to load course content</p>
        <p className="text-sm text-secondary-500">
          {(error as Error)?.message ||
            (modulesError as Error)?.message ||
            (moduleResourcesError as Error)?.message ||
            (isLearner ? (learnerProgressError as Error)?.message : undefined) ||
            'Please try again later.'}
        </p>
      </div>
    )
  }

  if (isLoading || isModulesLoading || isModuleResourcesLoading || (isLearner && isLearnerProgressLoading)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-secondary-500">Loading module…</div>
    )
  }

  if (!course) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-secondary-500">Course not found.</div>
    )
  }

  if (!sortedModules.length) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-secondary-500">
        <BookOpen className="mb-4 h-12 w-12 text-primary-400" />
        <p className="text-lg font-semibold">No modules available for this course yet.</p>
        <p className="text-sm text-secondary-500">Please check back once modules have been added.</p>
      </div>
    )
  }

  if (!activeModule) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-secondary-500">Loading module…</div>
    )
  }

  

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-primary-50 to-white">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 text-sm text-secondary-500">
              <Link to={paths.app.courses.detail.build(course.id)} className="inline-flex items-center gap-2 hover:text-secondary-700">
                <ArrowLeft className="h-4 w-4" />
                {course.title}
              </Link>
              <span className="text-secondary-300">/</span>
              <span className="inline-flex items-center gap-2 text-secondary-600">
                <Layers className="h-4 w-4" />
                Module {activeModule.order ?? '—'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Input
                value={resourceQuery}
                onChange={(event) => setResourceQuery(event.target.value)}
                placeholder="Search resources"
                icon={<Search className="h-4 w-4 text-primary-500" />}
                className="w-full sm:w-64"
              />
              {user && user.role !== UserRole.LEARNER && (
                <Button
                  variant="outline"
                  className="border-primary-200 text-primary-600 hover:bg-primary-50"
                  onClick={() => {
                    setUploadError(null)
                    setUploadSuccess(null)
                    setIsUploadModalOpen(true)
                  }}
                >
                  Upload Resource
                </Button>
              )}
            </div>
          </div>

          {uploadSuccess && (
            <div className="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {uploadSuccess}
            </div>
          )}

          <Card className="border-0 bg-white/80 shadow-lg backdrop-blur-sm">
            <CardHeader className="space-y-2">
              <Badge className="w-max rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary-600">
                {course.title}
              </Badge>
              <CardTitle className="text-3xl text-secondary-900">{activeModule.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="overflow-hidden rounded-3xl bg-black/5">
                {embedUrl ? (
                  <div className="relative aspect-video w-full overflow-hidden rounded-3xl bg-black">
                    <iframe
                      src={embedUrl}
                      loading="lazy"
                      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                      allowFullScreen
                      onContextMenu={handlePlayerContextMenu}
                      title={videoResource?.title ?? 'Course video player'}
                      className="h-full w-full"
                    />
                  </div>
                ) : fallbackVideoUrl ? (
                  <div className="relative aspect-video w-full overflow-hidden rounded-3xl bg-black">
                    <video controls className="h-full w-full object-cover">
                      <source src={fallbackVideoUrl} type={videoResource?.mimeType ?? 'video/mp4'} />
                      Your browser does not support embedded videos.
                    </video>
                  </div>
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center bg-gradient-to-br from-primary-200 to-purple-100">
                    <PlayCircle className="h-16 w-16 text-primary-500" />
                  </div>
                )}
              </div>

              <div className="grid gap-6 lg:grid-cols-[3fr,2fr]">
                <div className="space-y-4">
                  <Card className="border-0 bg-secondary-50">
                    <CardContent className="space-y-3 py-5">
                      <h3 className="text-lg font-semibold text-secondary-900">Summary</h3>
                      <p className="text-secondary-600">
                        {activeModule.description ??
                          'This module overview will appear here once your instructor provides a summary.'}
                      </p>
                    </CardContent>
                  </Card>

                  <ResourceList title="Downloads" resources={filteredDownloads} />
                  <ResourceList title="Additional Resources" resources={filteredLinks} />
                </div>

                <div className="space-y-4">
                  <Card className="border-0 bg-white shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg">Module Navigation</CardTitle>
                      <CardDescription>Jump between modules to navigate the course.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {sortedModules.map((module) => (
                        <button
                          key={module.id}
                          onClick={() => setModule(module)}
                className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                          module.id === activeModule.id
                            ? 'border-primary-300 bg-primary-50 text-primary-700'
                            : 'border-secondary-100 bg-secondary-50 hover:border-primary-200 hover:bg-primary-50'
                          }`}
                        >
                          <span className="block text-sm font-semibold">Module {module.order ?? '—'}</span>
                          <span className="block text-xs text-secondary-500">{module.title}</span>
                        </button>
                      ))}
                    </CardContent>
                  </Card>

                  {isLearner && (
                    <Card className="border-0 bg-white shadow-sm">
                      <CardHeader>
                        <CardTitle className="text-lg">Course Quizzes</CardTitle>
                        <CardDescription>Track the quizzes linked to this course.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {isQuizzesLoading ? (
                          <div className="flex min-h-[96px] items-center justify-center text-sm text-secondary-500">
                            Loading quizzes…
                          </div>
                        ) : availableQuizzes.length === 0 ? (
                          <div className="rounded-2xl border border-dashed border-secondary-200 bg-secondary-50 px-4 py-5 text-center text-sm text-secondary-500">
                            No quizzes have been published for this course yet.
                          </div>
                        ) : (
                          <>
                            <div className="space-y-3">
                              {availableQuizzes.slice(0, 3).map((quiz) => (
                                <div
                                  key={quiz.id}
                                  className="rounded-2xl border border-secondary-100 bg-secondary-50 px-4 py-3"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div>
                                      <p className="text-sm font-semibold text-secondary-900">{quiz.title}</p>
                                      <p className="text-xs text-secondary-500 line-clamp-2">
                                        {quiz.description ?? 'Your instructor will provide details soon.'}
                                      </p>
                                    </div>
                                    {quiz.dueDate && (
                                      <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-secondary-600">
                                        Due
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-secondary-600">
                                    <span className="inline-flex items-center gap-1.5">
                                      <AlarmClock className="h-3.5 w-3.5 text-secondary-400" />
                                      {formatQuizDueDate(quiz.dueDate)}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                      <Clock className="h-3.5 w-3.5 text-secondary-400" />
                                      {formatQuizTimeLimit(quiz.timeLimitSeconds)}
                                    </span>
                                    {quiz.maxAttempts && (
                                      <span className="inline-flex items-center gap-1.5">
                                        <CheckCircle className="h-3.5 w-3.5 text-secondary-400" />
                                        {quiz.maxAttempts} attempt{quiz.maxAttempts > 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-3">
                                    <Button
                                      asChild
                                      variant="ghost"
                                      size="sm"
                                      className="px-0 text-primary-600 hover:text-primary-700"
                                    >
                                      <Link
                                        to={{
                                          pathname: paths.app.quizzes.courseList.build(course.id),
                                          search: `?highlightQuizId=${quiz.id}`,
                                        }}
                                      >
                                        View quiz details
                                      </Link>
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {availableQuizzes.length > 3 && (
                              <Button
                                asChild
                                variant="outline"
                                className="w-full border-primary-200 text-primary-600 hover:bg-primary-50"
                              >
                                <Link to={paths.app.quizzes.courseList.build(course.id)}>
                                  View all quizzes ({availableQuizzes.length})
                                </Link>
                              </Button>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  <Card className="border-0 bg-white shadow-sm">
                    <CardContent className="space-y-3 py-5">
                      {isLearner && (
                        <>
                          <Button
                            onClick={handleMarkModuleCompleted}
                            disabled={isActiveModuleCompleted || markCompletedPending}
                            className={`w-full justify-center ${
                              isActiveModuleCompleted
                                ? 'bg-green-600 hover:bg-green-600'
                                : 'bg-primary-600 hover:bg-primary-700'
                            }`}
                          >
                            <CheckCircle className="mr-2 h-4 w-4" />
                            {isActiveModuleCompleted
                              ? 'Marked as Completed'
                              : markCompletedPending
                                ? 'Marking...'
                                : 'Mark as Completed'}
                          </Button>
                          {moduleActionError && (
                            <p className="text-xs text-red-600">{moduleActionError}</p>
                          )}
                        </>
                      )}
                      
                      <div className="flex items-center justify-between gap-3">
                        <Button
                          variant="outline"
                          className="flex-1 border-secondary-200 text-secondary-600 hover:bg-secondary-50"
                          onClick={() => setModule(previousModule)}
                          disabled={!previousModule}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          className="flex-1 border-secondary-200 text-secondary-600 hover:bg-secondary-50"
                          onClick={() => setModule(nextModule)}
                          disabled={!nextModule}
                        >
                          Next
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => {
          if (!uploadResourcesMutation.isPending) {
            setIsUploadModalOpen(false)
          }
        }}
        title="Upload Module Resource"
        size="lg"
      >
        <form
          className="space-y-5"
          onSubmit={async (event) => {
            event.preventDefault()
            if (!activeModule) {
              setUploadError('Select a module to attach the resources to.')
              return
            }
            if (!uploadTitle.trim()) {
              setUploadError('Please add a title for this resource.')
              return
            }
            if (uploadFiles.length === 0) {
              setUploadError('Select at least one file to upload.')
              return
            }
            setUploadError(null)
            try {
              await uploadResourcesMutation.mutateAsync({
                title: uploadTitle.trim(),
                description: uploadDescription.trim() || undefined,
                type: uploadType,
                files: uploadFiles,
                moduleId: activeModule.id,
              })
              setUploadSuccess('Resource uploaded successfully.')
              setUploadFiles([])
              setUploadTitle('')
              setUploadDescription('')
              setUploadType('document')
              setIsUploadModalOpen(false)
            } catch (mutationError) {
              setUploadError((mutationError as Error)?.message || 'Unable to upload resource.')
            }
          }}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700">Resource Title</label>
              <Input
                value={uploadTitle}
                onChange={(event) => setUploadTitle(event.target.value)}
                placeholder="e.g. Social Media Strategy Summary"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700">Resource Type</label>
              <Select value={uploadType} onChange={(event) => setUploadType(event.target.value)}>
                <option value="document">Document</option>
                <option value="video">Video</option>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">Description</label>
            <Textarea
              value={uploadDescription}
              onChange={(event) => setUploadDescription(event.target.value)}
              placeholder="Add a short description so learners know what to expect."
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">Files</label>
            <FileUpload
              multiple
              onFileSelect={(files) => setUploadFiles(files ? Array.from(files) : [])}
              helperText="Supported formats: PDF, DOCX, MP4. Maximum size 500MB per file."
            />
            {uploadFiles.length > 0 && (
              <ul className="text-sm text-secondary-600">
                {uploadFiles.map((file) => (
                  <li key={file.name}>{file.name}</li>
                ))}
              </ul>
            )}
          </div>
          {uploadError && <p className="text-sm text-primary-600">{uploadError}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="border-secondary-200 text-secondary-600 hover:bg-secondary-50"
              onClick={() => setIsUploadModalOpen(false)}
              disabled={uploadResourcesMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white"
              disabled={uploadResourcesMutation.isPending}
            >
              {uploadResourcesMutation.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  )
}
